/**
 * Debounced /estimate scheduler (reactive module, §10.6).
 *
 * Every request input (ship, range, columns, filters, freq, fill) calls
 * `scheduleEstimate()`; it debounces 400ms then POSTs /estimate and stores the
 * result in the `lastEstimate` atom. Consumers read that atom: the filter
 * match-count + zero-row diagnostic today (§10.5), the export stats strip once
 * §10.6 lands. It runs for EOS only (Export's source) and needs a ship + range -
 * those guards live here, not at the call sites. `syncToUrl()` is NOT triggered
 * here (unlike the blob, which folded it in); callers sync the URL themselves.
 */

import { estimate } from '../api/endpoints.ts';
import { buildExportRequest } from './request.ts';
import { activeDataSource, lastEstimate, estimatePending } from './store.ts';
import { hideError } from '../ui/error-box.ts';
import { DEFAULT_SOURCE } from './data-sources.ts';

const DEBOUNCE_MS = 400;
let timer: ReturnType<typeof setTimeout> | null = null;

export function scheduleEstimate(): void {
  if (timer !== null) clearTimeout(timer);
  estimatePending.set(true);
  timer = setTimeout(() => {
    timer = null;
    void fetchEstimate();
  }, DEBOUNCE_MS);
}

async function fetchEstimate(): Promise<void> {
  // The estimate sizes an export, and Export reads EOS only: with another
  // source the stats strip / match-count stay quiet.
  if (activeDataSource.value !== DEFAULT_SOURCE) {
    estimatePending.set(false);
    return;
  }

  const req = buildExportRequest();
  if (!req) {
    estimatePending.set(false);
    return;
  }

  try {
    const est = await estimate(req);
    lastEstimate.set(est);
    hideError();
  } catch {
    // Surface failure as "no estimate" rather than a stuck spinner; the ship
    // loader already shows hard errors. Match-count falls back to its empty state.
    lastEstimate.set(null);
  } finally {
    estimatePending.set(false);
  }
}
